/**
 * **Moving a visit that others follow** (visits §5.3, Board 10; phone Board
 * 17 cell 5) — a small prompt, not a surprise.
 *
 * Shown when Done would change a visit's first day and other visits follow
 * it. **Move all** (the default) saves the date and nothing else: the
 * followers' relations are stored, so they move by themselves. **Only this
 * one** first pins each *direct* follower to the day it had; their own
 * followers stay relative to them. Esc or a click outside cancels — the date
 * goes back and the visit's popover stays open.
 */
import { useEffect } from 'react'
import { usePhone } from '../../lib/use-narrow.js'
import { Popover } from '../../components/overlays.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { Button } from '../../components/primitives.js'
import { SHEET_BUTTON } from '../editor-shell.js'
import { cn } from '../../lib/cn.js'
import { IconFollows } from '../../frame/icons.js'
import { MOVE_PROMPT } from '../copy.js'

export interface MoveFollower { id: string; name: string; from: string | null; to: string | null }

const monDay = (iso: string | null): string => (iso
  ? new Date(`${iso.slice(0, 10)}T00:00:00Z`)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  : '—')
const dowMonDay = (iso: string): string => {
  const day = new Date(`${iso.slice(0, 10)}T00:00:00Z`)
  return `${day.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })} ${monDay(iso)}`
}

export function MovePrompt({
  open, anchor, name, to, followers, onMoveAll, onOnlyThis, onCancel,
}: {
  open: boolean
  anchor: HTMLElement | null
  /** The visit being moved, and the day it moves to. */
  name: string
  to: string
  /** Every visit that follows it, in walk order, with the day it had and the day it would have. */
  followers: MoveFollower[]
  onMoveAll: () => void
  onOnlyThis: () => void
  onCancel: () => void
}) {
  const phone = usePhone()

  useEffect(() => {
    if (!open || phone) return undefined
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, phone, onCancel])

  const body = (
    <>
      <p className="m-0 text-[13px] leading-[1.5] text-text">
        {MOVE_PROMPT.moving} <b>{name}</b> {MOVE_PROMPT.to} <b>{dowMonDay(to)}</b>. {MOVE_PROMPT.follow(followers.length)}
      </p>
      <div className="mt-2">
        {followers.map((one) => (
          <div key={one.id} data-move-follower=""
            className="flex items-center gap-2 border-t border-line-faint py-1.5 text-[12.5px]">
            <span aria-hidden className="flex shrink-0 text-info"><IconFollows size={13} /></span>
            <b className="font-semibold">{one.name}</b>
            <span className="tnum ml-auto text-text-muted">{monDay(one.from)} → {monDay(one.to)}</span>
          </div>
        ))}
      </div>
    </>
  )

  if (phone) {
    return (
      <PanelSheet open={open} onClose={onCancel} title={MOVE_PROMPT.sheetTitle} sub={MOVE_PROMPT.sheetSub} layer={80}>
        {/*
          * cb-17 · 5: 50 / 50 / 44, radius 14, gap 8, 14px under the
          * followers. Important heights, because `Button`'s own `h-8` sits in
          * the same class string — which is how these measured 32.
          */}
        <div className="flex flex-col gap-2 px-4 pb-4 pt-2">
          {body}
          <Button variant="primary" className={cn(SHEET_BUTTON, 'mt-1.5 !h-[50px] w-full !text-[15px]')}
            onClick={onMoveAll}>
            {MOVE_PROMPT.moveAll(followers.length)}
          </Button>
          <Button className={cn(SHEET_BUTTON, '!h-[50px] w-full !text-[15px]')} onClick={onOnlyThis}>
            {MOVE_PROMPT.onlyThis}
          </Button>
          <button type="button" onClick={onCancel}
            className="h-11 w-full rounded-record text-[14px] font-semibold text-text">
            {MOVE_PROMPT.cancel}
          </button>
        </div>
      </PanelSheet>
    )
  }

  return (
    <Popover open={open} onClose={onCancel} anchor={anchor} width={420} label={MOVE_PROMPT.sheetTitle}
      className="px-[18px] py-4" layer={2}>
      <div data-move-prompt="">
        {body}
        <div className="mt-3.5 flex items-center gap-2">
          <Button variant="primary" className="h-[34px] text-[12.5px] font-bold" onClick={onMoveAll}>
            {MOVE_PROMPT.moveAll(followers.length)}
          </Button>
          <Button className="h-[34px] text-[12.5px] font-bold" onClick={onOnlyThis}>{MOVE_PROMPT.onlyThis}</Button>
          <span className="ml-auto self-center text-[12px] text-text-muted">{MOVE_PROMPT.escape}</span>
        </div>
      </div>
    </Popover>
  )
}
